// Types for pipeline state
interface PipelineColumn {
	_id: string;
	name: string;
	position: number;
	color: string;
	itemLimit: number | null;
	// Automatic customer text when a card enters this column
	onEnterSms?: {
		enabled: boolean;
		message: string;
	};
}

interface PropertyDefinition {
	id: string;
	name: string;
	type: 'text' | 'date' | 'checkbox' | 'number' | 'select';
	options?: string[];
	required?: boolean;
	isStarred?: boolean;
	position: number;
	createdAt?: Date;
}

interface PipelineItem {
	_id: string;
	_business: string;
	_pipeline: string;
	columnId: string;
	name: string;
	itemNumber?: number | null;
	description?: string;
	position: number;
	_assignee?: string | null;
	_assignedBy?: string;
	assignedAt?: Date;
	_customer?: string | null;
	_seenBy?: string[];
	source?: 'user' | 'customer';
	priority: 'low' | 'medium' | 'high' | 'urgent';
	status: 'active' | 'completed' | 'archived' | 'on-hold';
	dueDate?: Date | null;
	completedAt?: Date;
	_completedBy?: string;
	tags?: string[];
	color?: string | null;
	_teamConversation?: string | null;
	customFields?: Record<string, any>;
	linkedEntities?: Array<{
		entityType:
			| 'customer'
			| 'message'
			| 'review'
			| 'campaign'
			| 'form_submission';
		_entity: string;
	}>;
	_createdBy: string;
	_lastModifiedBy?: string;
	movedAt?: Date;
	createdAt?: Date;
	updatedAt?: Date;
	isOverdue?: boolean;
	// Most recent automatic stage-change text queued for this card
	lastStageSms?: {
		columnId: string | null;
		sentAt: Date | null;
	};
}

interface Pipeline {
	_id: string;
	_business: string;
	name: string;
	description?: string;
	columns: PipelineColumn[];
	propertyDefinitions?: PropertyDefinition[];
	isActive: boolean;
	settings?: {
		allowItemCreation?: boolean;
		allowColumnManagement?: boolean;
		defaultAssignee?: string | null;
		sendReviewRequestOnComplete?: boolean;
	};
	_createdBy?: string;
	_lastModifiedBy?: string;
	createdAt?: Date;
	updatedAt?: Date;
}

interface PipelineSummary {
	assignedActiveCount: number;
	hasAssigneeUnseen: boolean;
	hasBusinessUnseen: boolean;
	hasUnseen: boolean;
}

// Read-state of a customer's newest conversation, keyed by customer id.
// isRead === false means the customer messaged and nobody has read it yet.
interface ConversationState {
	isRead: boolean;
	latestMessageDate?: string | null;
}

interface PipelineState {
	pipelines: Pipeline[];
	currentPipeline: Pipeline | null;
	items: { [pipelineId: string]: PipelineItem[] };
	loading: boolean;
	error: string | null;
	selectedItems: string[];
	draggedItem: PipelineItem | null;
	summary: PipelineSummary;
	conversationsByCustomer: { [customerId: string]: ConversationState };
}

const initialState: PipelineState = {
	pipelines: [],
	currentPipeline: null,
	items: {},
	loading: false,
	error: null,
	selectedItems: [],
	draggedItem: null,
	summary: {
		assignedActiveCount: 0,
		hasAssigneeUnseen: false,
		hasBusinessUnseen: false,
		hasUnseen: false,
	},
	conversationsByCustomer: {},
};

// Define action types
type PipelineAction = {
	type: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload?: any;
};

const pipelineReducer = (
	state = initialState,
	action: PipelineAction
): PipelineState => {
	switch (action.type) {
		case 'SET_PIPELINES':
			return { ...state, pipelines: action.payload, error: null };
		case 'SET_CURRENT_PIPELINE':
			// Ensure propertyDefinitions exists
			const pipeline = action.payload;
			if (pipeline && !pipeline.propertyDefinitions) {
				pipeline.propertyDefinitions = [];
			}
			return { ...state, currentPipeline: pipeline, error: null };
		case 'SET_PIPELINE_ITEMS':
			// Ensure each item has customFields object
			const items = action.payload.items.map((item: PipelineItem) => ({
				...item,
				customFields: item.customFields || {},
			}));
			return {
				...state,
				items: {
					...state.items,
					[action.payload.pipelineId]: items,
				},
				error: null,
			};
		case 'MARK_PIPELINE_ITEMS_SEEN': {
			const { pipelineId, userId } = action.payload;
			const pipelineItems = state.items[pipelineId];
			if (!pipelineItems || !userId) return state;
			return {
				...state,
				items: {
					...state.items,
					[pipelineId]: pipelineItems.map((item) =>
						item._seenBy?.includes(userId)
							? item
							: {
									...item,
									_seenBy: [...(item._seenBy || []), userId],
							  }
					),
				},
			};
		}
		case 'SET_CONVERSATION_STATES':
			return { ...state, conversationsByCustomer: action.payload || {} };
		case 'UPDATE_CONVERSATION_STATE': {
			const { customerId, isRead, latestMessageDate } = action.payload;
			if (!customerId) return state;
			return {
				...state,
				conversationsByCustomer: {
					...state.conversationsByCustomer,
					[customerId]: { isRead, latestMessageDate },
				},
			};
		}
		case 'SET_LOADING':
			return { ...state, loading: action.payload };
		case 'SET_ERROR':
			return { ...state, error: action.payload, loading: false };
		case 'ADD_PIPELINE':
			return {
				...state,
				pipelines: [...state.pipelines, action.payload],
				error: null,
			};
		case 'UPDATE_PIPELINE':
			const updatedPipeline = {
				...action.payload,
				propertyDefinitions: action.payload.propertyDefinitions || [],
			};
			return {
				...state,
				pipelines: state.pipelines.map((p) =>
					p._id === action.payload._id ? updatedPipeline : p
				),
				currentPipeline:
					state.currentPipeline?._id === action.payload._id
						? updatedPipeline
						: state.currentPipeline,
				error: null,
			};
		case 'DELETE_PIPELINE': {
			const { [action.payload]: _removedItems, ...remainingItems } =
				state.items;
			return {
				...state,
				pipelines: state.pipelines.filter(
					(p) => p._id !== action.payload
				),
				items: remainingItems,
				currentPipeline:
					state.currentPipeline?._id === action.payload
						? null
						: state.currentPipeline,
				error: null,
			};
		}
		case 'ADD_ITEM': {
			const { pipelineId, item } = action.payload;
			const newItem = {
				...item,
				customFields: item.customFields || {},
			};
			return {
				...state,
				items: {
					...state.items,
					[pipelineId]: [...(state.items[pipelineId] || []), newItem],
				},
				error: null,
			};
		}
		case 'UPDATE_ITEM':
			return {
				...state,
				items: {
					...state.items,
					[action.payload.pipelineId]:
						state.items[action.payload.pipelineId]?.map((item) =>
							item._id === action.payload.item._id
								? {
										...item, // Keep all existing fields
										...action.payload.item, // Merge with updates
										customFields: {
											...(item.customFields || {}),
											...(action.payload.item
												.customFields || {}),
										},
								  }
								: item
						) || [],
				},
				error: null,
			};
		case 'UPDATE_ITEM_CUSTOM_FIELD': {
			const { pipelineId, itemId, propertyId, value } = action.payload;
			return {
				...state,
				items: {
					...state.items,
					[pipelineId]:
						state.items[pipelineId]?.map((item) => {
							if (item._id === itemId) {
								const newCustomFields = {
									...(item.customFields || {}),
								};
								if (
									value === null ||
									value === undefined ||
									value === ''
								) {
									delete newCustomFields[propertyId];
								} else {
									newCustomFields[propertyId] = value;
								}
								return {
									...item,
									customFields: newCustomFields,
								};
							}
							return item;
						}) || [],
				},
				error: null,
			};
		}
		case 'MOVE_ITEM': {
			const { itemId, fromColumn, toColumn, position } = action.payload;
			const pId = action.payload.pipelineId;
			const pipelineItems = state.items[pId] || [];

			// Find the item being moved
			const movingItem = pipelineItems.find(
				(item) => item._id === itemId
			);
			if (!movingItem) {
				return state;
			}

			const oldPosition = movingItem.position;

			return {
				...state,
				items: {
					...state.items,
					[pId]: pipelineItems.map((item) => {
						// The item being moved
						if (item._id === itemId) {
							return { ...item, columnId: toColumn, position };
						}

						// Moving within the same column
						if (fromColumn === toColumn) {
							// Items in the same column that need position adjustment
							if (
								item.columnId === fromColumn &&
								item._id !== itemId
							) {
								if (oldPosition < position) {
									// Moving down: shift items between old and new position up
									if (
										item.position > oldPosition &&
										item.position <= position
									) {
										return {
											...item,
											position: item.position - 1,
										};
									}
								} else if (oldPosition > position) {
									// Moving up: shift items between new and old position down
									if (
										item.position >= position &&
										item.position < oldPosition
									) {
										return {
											...item,
											position: item.position + 1,
										};
									}
								}
							}
						} else {
							// Moving to a different column
							// Close gap in source column
							if (
								item.columnId === fromColumn &&
								item.position > oldPosition
							) {
								return { ...item, position: item.position - 1 };
							}
							// Make space in target column
							if (
								item.columnId === toColumn &&
								item.position >= position
							) {
								return { ...item, position: item.position + 1 };
							}
						}

						return item;
					}),
				},
				error: null,
			};
		}
		case 'DELETE_ITEM':
			return {
				...state,
				items: {
					...state.items,
					[action.payload.pipelineId]:
						state.items[action.payload.pipelineId]?.filter(
							(item) => item._id !== action.payload.itemId
						) || [],
				},
				error: null,
			};
		case 'SET_PIPELINE_SUMMARY':
			return { ...state, summary: action.payload };
		case 'SET_SELECTED_ITEMS':
			return { ...state, selectedItems: action.payload };
		case 'SET_DRAGGED_ITEM':
			return { ...state, draggedItem: action.payload };
		case 'CLEAR_PIPELINE_STATE':
			return initialState;
		default:
			return state;
	}
};

export default pipelineReducer;
export type {
	PipelineState,
	PipelineSummary,
	Pipeline,
	PipelineItem,
	PipelineColumn,
	PropertyDefinition,
};
